import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Users,
  Clock,
  Calendar as CalendarIcon,
  UserCheck,
  ArrowUpRight,
  ArrowDownRight,
  Hash,
  Camera,
  MapPin,
  Mail,
  AlertTriangle,
  ShieldAlert,
  ArrowLeftRight,
  ClipboardList,
  CalendarPlus,
  UserPlus,
  History,
  CreditCard,
  CheckCircle2,
  Activity,
  ChevronRight,
  CalendarClock,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { formatUK } from '../../lib/ukTime';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Small presentational sub-components (kept in-file for cohesion) ----------

function KpiCard({ title, value, icon: Icon, tone = 'neutral', onClick, testId }) {
  const toneStyles = {
    neutral: 'text-foreground bg-muted/60',
    blue: 'text-blue-500 bg-blue-500/10',
    green: 'text-green-500 bg-green-500/10',
    primary: 'text-primary bg-primary/10',
    amber: 'text-amber-500 bg-amber-500/10',
    purple: 'text-purple-500 bg-purple-500/10',
    red: 'text-red-500 bg-red-500/10',
  };
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
      className={cn(
        'bg-card border-border rounded-sm transition-colors',
        onClick && 'cursor-pointer hover:border-primary/50 hover:bg-accent/40 focus:outline-none focus:ring-2 focus:ring-primary/40'
      )}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className={cn('p-1.5 sm:p-2 rounded-sm flex-shrink-0', toneStyles[tone])}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
          </div>
          <span className="font-mono text-xl sm:text-2xl font-medium text-foreground">{value}</span>
        </div>
        <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mt-2 sm:mt-3 leading-tight">
          {title}
        </p>
      </CardContent>
    </Card>
  );
}

function AttentionRow({ label, count, severity, onClick, testId }) {
  const styles = {
    critical: 'border-red-500/40 bg-red-500/5 text-red-500',
    warning: 'border-amber-500/40 bg-amber-500/5 text-amber-500',
    clear: 'border-green-500/40 bg-green-500/5 text-green-500',
  };
  const dotColor = {
    critical: 'bg-red-500',
    warning: 'bg-amber-500',
    clear: 'bg-green-500',
  };
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 rounded-sm border text-left transition-colors hover:bg-accent/40',
        styles[severity]
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={cn('w-2 h-2 rounded-full flex-shrink-0', dotColor[severity])} />
        <span className="text-sm font-medium text-foreground truncate">{label}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge className={cn(
          'text-xs font-mono px-2 py-0.5 rounded-sm border',
          severity === 'critical' && 'bg-red-500/15 text-red-500 border-red-500/40',
          severity === 'warning' && 'bg-amber-500/15 text-amber-500 border-amber-500/40',
          severity === 'clear' && 'bg-green-500/15 text-green-500 border-green-500/40',
        )}>
          {count}
        </Badge>
        <ChevronRight className="w-4 h-4 opacity-60" />
      </div>
    </button>
  );
}

function QuickActionButton({ label, icon: Icon, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group flex flex-col items-start justify-between gap-3 p-3 sm:p-4 bg-card border border-border rounded-sm hover:border-primary/60 hover:bg-accent/40 transition-colors text-left min-h-[88px] sm:min-h-[104px]"
    >
      <div className="p-2 rounded-sm bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
      </div>
      <span className="text-xs sm:text-sm font-barlow uppercase tracking-wider text-foreground leading-tight">
        {label}
      </span>
    </button>
  );
}

function activityIconFor(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('claim') && t.includes('un')) return { icon: ArrowDownRight, color: 'text-amber-500 bg-amber-500/10' };
  if (t.includes('claim')) return { icon: ArrowUpRight, color: 'text-green-500 bg-green-500/10' };
  if (t.includes('swap')) return { icon: ArrowLeftRight, color: 'text-blue-500 bg-blue-500/10' };
  if (t.includes('manual')) return { icon: ShieldAlert, color: 'text-red-500 bg-red-500/10' };
  if (t.includes('edit') || t.includes('admin')) return { icon: FileText, color: 'text-purple-500 bg-purple-500/10' };
  if (t.includes('clock_in') || t === 'clockin') return { icon: ArrowUpRight, color: 'text-green-500 bg-green-500/10' };
  if (t.includes('clock_out') || t === 'clockout') return { icon: ArrowDownRight, color: 'text-red-500 bg-red-500/10' };
  return { icon: Activity, color: 'text-muted-foreground bg-muted/60' };
}

// ---------- Main page ----------

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Additional KPIs (computed from existing endpoints, no backend changes)
  const [openShiftsCount, setOpenShiftsCount] = useState(0);
  const [reviewsRequired, setReviewsRequired] = useState({
    manual_clock_ins: 0,
    auto_clock_outs: 0,
    missing_gps: 0,
    total: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);

  // Clock in/out states (kept exactly as before)
  const [employeeCode, setEmployeeCode] = useState('');
  const [clockLoading, setClockLoading] = useState(false);
  const [recentClockEvents, setRecentClockEvents] = useState([]);
  const [captureModal, setCaptureModal] = useState({ open: false, eventType: null, photo: null });

  // Drill-down details + photo modal
  const [detailsModal, setDetailsModal] = useState({ open: false, type: null, data: null, loading: false });
  const [viewPhotoModal, setViewPhotoModal] = useState({ open: false, photo: null, name: '', timestamp: null, loading: false });

  const viewClockInPhoto = async (eventId, name, timestamp) => {
    setViewPhotoModal({ open: true, photo: null, name, timestamp, loading: true });
    try {
      const res = await axios.get(`${API}/admin/clock/photo/${eventId}`);
      if (res.data.photo_url && res.data.photo_url !== 'has_photo') {
        setViewPhotoModal({ open: true, photo: res.data.photo_url, name, timestamp, loading: false });
      } else {
        setViewPhotoModal({ open: false, photo: null, name: '', timestamp: null, loading: false });
        toast.error('No photo available');
      }
    } catch (err) {
      setViewPhotoModal({ open: false, photo: null, name: '', timestamp: null, loading: false });
      toast.error('Failed to load photo');
    }
  };

  const openDetails = async (type) => {
    setDetailsModal({ open: true, type, data: null, loading: true });
    try {
      const endpointMap = {
        employees: 'employees',
        on_shift: 'on-shift',
        today_shifts: 'today-shifts',
      };
      const ep = endpointMap[type];
      if (!ep) {
        setDetailsModal({ open: false, type: null, data: null, loading: false });
        return;
      }
      const res = await axios.get(`${API}/stats/dashboard/details/${ep}`);
      setDetailsModal((p) => ({ ...p, data: res.data, loading: false }));
    } catch (err) {
      toast.error('Failed to load details');
      setDetailsModal({ open: false, type: null, data: null, loading: false });
    }
  };

  useEffect(() => {
    fetchAll();
    axios.post(`${API}/admin/sync-shifts`).catch(() => {});
  }, []);

  useAutoRefresh(() => fetchAll(true));

  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [statsRes, openRes, notifsRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/open-shifts`).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications?limit=10`).catch(() => ({ data: [] })),
      ]);

      setStats(statsRes.data);

      // Open shifts = total unfilled spots across non-archived open_shifts
      const today = new Date().toISOString().slice(0, 10);
      const openCount = (openRes.data || []).reduce((acc, s) => {
        const req = s.required_staff || 0;
        const claimed = s.claimed_count || (s.claimed_by ? s.claimed_by.length : 0) || 0;
        const remaining = Math.max(0, req - claimed);
        // Only count current/future days (skip past unfilled to keep number relevant)
        if (!s.date || s.date >= today) return acc + remaining;
        return acc;
      }, 0);
      setOpenShiftsCount(openCount);

      setRecentActivity(notifsRes.data || []);

      // Reviews Required — pull last 14 days clock events and count flags
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 14);
      const startStr = start.toISOString().slice(0, 10);
      const endStr = end.toISOString().slice(0, 10);
      try {
        const repRes = await axios.get(
          `${API}/admin/clock/report?start_date=${startStr}&end_date=${endStr}`
        );
        let manual = 0, auto = 0, missingGps = 0;
        const employees = repRes.data?.employees || [];
        for (const emp of employees) {
          for (const ev of emp.events || []) {
            if (ev.allocated_by) manual += 1; // manual clock-ins flagged via allocated_by
            if (ev.auto_clock_out) auto += 1;
            if (ev.latitude == null && ev.longitude == null && ev.type === 'clock_in') missingGps += 1;
          }
        }
        const total = manual + auto + missingGps;
        setReviewsRequired({ manual_clock_ins: manual, auto_clock_outs: auto, missing_gps: missingGps, total });
      } catch (e) {
        // Non-fatal — keep zeros
        setReviewsRequired({ manual_clock_ins: 0, auto_clock_outs: 0, missing_gps: 0, total: 0 });
      }
    } catch (err) {
      console.error('Dashboard fetch failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setCaptureModal((prev) => ({ ...prev, photo: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleClockIn = () => {
    if (!employeeCode || employeeCode.length !== 4) {
      toast.error('Please enter a valid 4-digit employee code');
      return;
    }
    setCaptureModal({ open: true, eventType: 'clock_in', photo: null });
  };

  const handleClockOut = () => {
    if (!employeeCode || employeeCode.length !== 4) {
      toast.error('Please enter a valid 4-digit employee code');
      return;
    }
    setCaptureModal({ open: true, eventType: 'clock_out', photo: null });
  };

  const executeClockWithPhoto = async () => {
    if (!captureModal.photo) {
      toast.error('Please take a photo before clocking');
      return;
    }
    setClockLoading(true);
    try {
      const response = await axios.post(`${API}/supervisor/clock`, {
        employee_code: employeeCode,
        event_type: captureModal.eventType,
      });
      await axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: captureModal.photo,
      });
      toast.success(response.data.message || `Employee clocked ${captureModal.eventType === 'clock_in' ? 'in' : 'out'} successfully`);
      setRecentClockEvents((prev) => [{
        ...response.data,
        photo_url: captureModal.photo,
        timestamp: new Date().toISOString(),
      }, ...prev].slice(0, 5));
      setEmployeeCode('');
      setCaptureModal({ open: false, eventType: null, photo: null });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Clock event failed');
    } finally {
      setClockLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  // ---------- KPI cards ----------
  const kpis = [
    { title: 'Total Employees', value: stats?.total_employees || 0, icon: Users, tone: 'blue', onClick: () => openDetails('employees'), testId: 'kpi-total-employees' },
    { title: 'On Shift Now', value: stats?.on_shift_count || 0, icon: UserCheck, tone: 'green', onClick: () => openDetails('on_shift'), testId: 'kpi-on-shift-now' },
    { title: "Today's Shifts", value: stats?.todays_shifts || 0, icon: CalendarIcon, tone: 'primary', onClick: () => openDetails('today_shifts'), testId: 'kpi-today-shifts' },
    { title: 'Open Shifts', value: openShiftsCount, icon: CalendarClock, tone: 'amber', onClick: () => navigate('/admin/shifts/events'), testId: 'kpi-open-shifts' },
    { title: 'Reviews Required', value: reviewsRequired.total, icon: ShieldAlert, tone: 'red', onClick: () => navigate('/admin/clock-history'), testId: 'kpi-reviews-required' },
  ];

  // ---------- Requires Attention rows ----------
  const attentionItems = [
    {
      label: 'Manual clock-ins awaiting review',
      count: reviewsRequired.manual_clock_ins,
      severity: reviewsRequired.manual_clock_ins > 0 ? 'critical' : 'clear',
      onClick: () => navigate('/admin/clock-history'),
      testId: 'attn-manual-clockins',
    },
    {
      label: 'Auto clock-outs (last 14 days)',
      count: reviewsRequired.auto_clock_outs,
      severity: reviewsRequired.auto_clock_outs > 0 ? 'critical' : 'clear',
      onClick: () => navigate('/admin/clock-history'),
      testId: 'attn-auto-clockouts',
    },
    {
      label: 'Clock-ins missing GPS',
      count: reviewsRequired.missing_gps,
      severity: reviewsRequired.missing_gps > 0 ? 'warning' : 'clear',
      onClick: () => navigate('/admin/clock-history'),
      testId: 'attn-missing-gps',
    },
    {
      label: 'Unclaimed shifts',
      count: openShiftsCount,
      severity: openShiftsCount > 0 ? 'warning' : 'clear',
      onClick: () => navigate('/admin/shifts/events'),
      testId: 'attn-unclaimed-shifts',
    },
    {
      label: 'Employees with unsigned policies',
      count: stats?.unsigned_policies_count ?? null, // not currently exposed — leave as informational link
      severity: 'warning',
      onClick: () => navigate('/admin/legal'),
      testId: 'attn-unsigned-policies',
    },
  ];

  const allClear = attentionItems.every((i) => (i.count === 0 || i.count === null) && i.label !== 'Employees with unsigned policies') && reviewsRequired.total === 0 && openShiftsCount === 0;

  // ---------- Quick Actions ----------
  const quickActions = [
    { label: 'Create Event', icon: CalendarPlus, onClick: () => navigate('/admin/events/create'), testId: 'qa-create-event' },
    { label: 'Add Employee', icon: UserPlus, onClick: () => navigate('/admin/employees'), testId: 'qa-add-employee' },
    { label: 'Event Calendar', icon: CalendarIcon, onClick: () => navigate('/admin/shifts/events'), testId: 'qa-upcoming-shifts' },
    { label: 'Clock History', icon: History, onClick: () => navigate('/admin/clock-history'), testId: 'qa-clock-history' },
    { label: 'Staff QR Cards', icon: CreditCard, onClick: () => navigate('/admin/feedback-cards'), testId: 'qa-staff-qr-cards' },
  ];

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6 space-y-5 sm:space-y-6 grid-pattern min-h-screen">
        {/* Header */}
        <div className="border-b border-border pb-4 sm:pb-6">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground">
            COMMAND CENTER
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        {/* KPI Grid: 2 per row on mobile, 3 on tablet, 6 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4" data-testid="dashboard-kpi-grid">
          {kpis.map((k) => (
            <KpiCard key={k.title} {...k} />
          ))}
        </div>

        {/* Requires Attention */}
        <Card className="bg-card border-border rounded-sm" data-testid="requires-attention-section">
          <CardHeader className="border-b border-border pb-3 sm:pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
              Requires Attention
              {allClear && (
                <Badge className="ml-auto bg-green-500/15 text-green-500 border border-green-500/40 text-[10px] uppercase tracking-wider">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> All clear
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
              {attentionItems.map((item) => (
                <AttentionRow
                  key={item.label}
                  label={item.label}
                  count={item.count === null ? '—' : item.count}
                  severity={item.count === null ? 'warning' : item.severity}
                  onClick={item.onClick}
                  testId={item.testId}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-card border-border rounded-sm" data-testid="quick-actions-section">
          <CardHeader className="border-b border-border pb-3 sm:pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-primary" strokeWidth={1.5} />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {quickActions.map((qa) => (
                <QuickActionButton key={qa.label} {...qa} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-card border-border rounded-sm" data-testid="recent-activity-section">
          <CardHeader className="border-b border-border pb-3 sm:pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" strokeWidth={1.5} />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No recent activity</p>
            ) : (
              <div className="space-y-1.5">
                {recentActivity.map((n) => {
                  const { icon: Ico, color } = activityIconFor(n.type);
                  return (
                    <div
                      key={n.id}
                      className={cn(
                        'flex items-start gap-3 px-2 sm:px-3 py-2 rounded-sm transition-colors hover:bg-accent/40',
                        !n.read && 'bg-primary/5'
                      )}
                      data-testid={`activity-${n.id}`}
                    >
                      <div className={cn('p-1.5 rounded-sm flex-shrink-0', color)}>
                        <Ico className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground leading-snug truncate">
                          {n.message}
                        </p>
                        {n.employee_name && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            {n.employee_name}
                          </p>
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground font-mono flex-shrink-0">
                        {formatUK(n.created_at, 'HH:mm')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Clock In / Out (moved to the bottom) */}
        <Card className="bg-card border-border rounded-sm" data-testid="quick-clock-section">
          <CardHeader className="border-b border-border pb-3 sm:pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" strokeWidth={1.5} />
              Quick Clock In / Out
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 max-w-2xl mx-auto">
              <div className="w-full sm:flex-1 space-y-2">
                <Label htmlFor="admin-employee-code" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Employee Code
                </Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="admin-employee-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={employeeCode}
                    onChange={(e) => setEmployeeCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="0000"
                    className="pl-10 text-center text-2xl font-mono tracking-[0.5em] h-12 sm:h-14 bg-background border-border rounded-sm"
                    data-testid="admin-employee-code-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  onClick={handleClockIn}
                  disabled={clockLoading || employeeCode.length !== 4}
                  className="flex-1 sm:flex-none h-12 sm:h-14 px-4 sm:px-6 bg-green-600 hover:bg-green-500 text-foreground rounded-sm font-barlow tracking-wider"
                  data-testid="admin-clock-in-btn"
                >
                  <ArrowUpRight className="w-5 h-5 mr-2" />
                  CLOCK IN
                </Button>
                <Button
                  onClick={handleClockOut}
                  disabled={clockLoading || employeeCode.length !== 4}
                  className="flex-1 sm:flex-none h-12 sm:h-14 px-4 sm:px-6 bg-red-600 hover:bg-red-500 text-foreground rounded-sm font-barlow tracking-wider"
                  data-testid="admin-clock-out-btn"
                >
                  <ArrowDownRight className="w-5 h-5 mr-2" />
                  CLOCK OUT
                </Button>
              </div>
            </div>

            {recentClockEvents.length > 0 && (
              <div className="mt-5 pt-4 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Recent Admin Clock Actions
                </p>
                <div className="space-y-2">
                  {recentClockEvents.map((event, index) => (
                    <div
                      key={index}
                      className={cn(
                        'flex items-center justify-between p-2.5 sm:p-3 rounded-sm',
                        event.event_type === 'clock_in'
                          ? 'bg-green-900/20 border border-green-900/50'
                          : 'bg-red-900/20 border border-red-900/50'
                      )}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {event.event_type === 'clock_in' ? (
                          <ArrowUpRight className="w-4 h-4 text-green-400 flex-shrink-0" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-sm truncate">{event.employee_name}</span>
                        <Badge className={event.event_type === 'clock_in' ? 'bg-green-600 text-xs flex-shrink-0' : 'bg-red-600 text-xs flex-shrink-0'}>
                          {event.event_type === 'clock_in' ? 'In' : 'Out'}
                        </Badge>
                        {event.photo_url && <Camera className="w-3 h-3 text-blue-400 flex-shrink-0" />}
                      </div>
                      <span className="text-xs text-muted-foreground font-mono flex-shrink-0">
                        {formatUK(event.timestamp, 'HH:mm')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drill-down modal (same behaviour as before) */}
      <Dialog
        open={detailsModal.open}
        onOpenChange={(open) => { if (!open) setDetailsModal({ open: false, type: null, data: null, loading: false }); }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">
              {detailsModal.type === 'employees' && `All Employees (${detailsModal.data?.count || 0})`}
              {detailsModal.type === 'on_shift' && `On Shift Now (${detailsModal.data?.count || 0})`}
              {detailsModal.type === 'today_shifts' && `Today's Shifts (${detailsModal.data?.count || 0})`}
            </DialogTitle>
          </DialogHeader>
          {detailsModal.loading ? (
            <p className="text-sm text-muted-foreground p-6 text-center">Loading...</p>
          ) : !detailsModal.data?.items?.length ? (
            <p className="text-sm text-muted-foreground p-6 text-center">No records to show</p>
          ) : (
            <div className="space-y-2 mt-2">
              {detailsModal.type === 'employees' && detailsModal.data.items.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 bg-background rounded-sm border border-border">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-border">{u.role}</Badge>
                    <Badge className={cn('text-[10px] uppercase tracking-wider', u.status === 'active' ? 'status-active' : 'status-inactive')}>
                      {u.status}
                    </Badge>
                  </div>
                </div>
              ))}

              {detailsModal.type === 'on_shift' && detailsModal.data.items.map((s) => (
                <div key={s.employee_id} className="p-3 bg-background rounded-sm border border-border space-y-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm font-medium">{s.name}</p>
                    <div className="flex items-center gap-2">
                      {s.clock_in_time && (
                        <span className="text-xs font-mono text-green-500">
                          IN {formatUK(s.clock_in_time, 'HH:mm')}
                        </span>
                      )}
                      {s.clock_event_id && s.has_photo && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewClockInPhoto(s.clock_event_id, s.name, s.clock_in_time)}
                          className="h-7 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                          data-testid={`view-photo-${s.employee_id}`}
                          title="View Clock-In Photo"
                        >
                          <Camera className="w-3.5 h-3.5 mr-1" />
                          <span className="text-xs">Photo</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                    {s.position && <span className="uppercase tracking-wider">{s.position}</span>}
                    {s.site_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.site_name}</span>}
                    {(s.start_time && s.end_time) ? (
                      <span className="font-mono">{s.start_time} - {s.end_time}</span>
                    ) : (
                      <span className="text-amber-400 font-semibold uppercase tracking-wider text-[10px] px-1 bg-amber-500/10 border border-amber-500/30 rounded-sm">Pending</span>
                    )}
                  </div>
                </div>
              ))}

              {detailsModal.type === 'today_shifts' && detailsModal.data.items.map((s) => (
                <div key={s.shift_id} className="p-3 bg-background rounded-sm border border-border space-y-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm font-medium">{s.employee_name || 'Unassigned'}</p>
                    <Badge className={cn(
                      'text-[10px] uppercase tracking-wider',
                      s.status === 'completed' ? 'status-active' :
                      s.status === 'in_progress' ? 'bg-primary/20 text-primary border border-primary/30' :
                      'status-inactive'
                    )}>
                      {(s.status || 'scheduled').replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                    {s.position && <span className="uppercase tracking-wider">{s.position}</span>}
                    {s.site_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.site_name}</span>}
                    {(s.start_time && s.end_time) ? (
                      <span className="font-mono">{s.start_time} - {s.end_time}</span>
                    ) : (
                      <span className="text-amber-400 font-semibold uppercase tracking-wider text-[10px] px-1 bg-amber-500/10 border border-amber-500/30 rounded-sm">Pending</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo capture modal (unchanged) */}
      <Dialog open={captureModal.open} onOpenChange={(open) => {
        if (!open) setCaptureModal({ open: false, eventType: null, photo: null });
      }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              {captureModal.eventType === 'clock_in' ? 'Clock In' : 'Clock Out'} - Photo Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground text-center">
              Take a photo of the staff member to {captureModal.eventType === 'clock_in' ? 'clock in' : 'clock out'}
            </p>
            {captureModal.photo ? (
              <div className="space-y-3">
                <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
                  <img src={captureModal.photo} alt="Captured" className="w-full h-full object-cover" />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCaptureModal((prev) => ({ ...prev, photo: null }))}
                  className="w-full border-border rounded-sm"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Retake Photo
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <label
                  htmlFor="admin-photo-capture"
                  className="flex flex-col items-center justify-center aspect-video bg-muted rounded-sm border-2 border-dashed border-border cursor-pointer hover:border-primary/50 transition-colors"
                >
                  <Camera className="w-12 h-12 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Tap to take staff photo</p>
                  <p className="text-xs text-muted-foreground mt-1">Photo is required</p>
                </label>
                <input
                  id="admin-photo-capture"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCaptureModal({ open: false, eventType: null, photo: null })}
                className="flex-1 border-border rounded-sm"
                disabled={clockLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={executeClockWithPhoto}
                disabled={!captureModal.photo || clockLoading}
                className={cn(
                  'flex-1 rounded-sm',
                  captureModal.eventType === 'clock_in'
                    ? 'bg-green-600 hover:bg-green-500 text-foreground'
                    : 'bg-red-600 hover:bg-red-500 text-foreground',
                  !captureModal.photo && 'opacity-50 cursor-not-allowed'
                )}
              >
                {clockLoading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : captureModal.eventType === 'clock_in' ? (
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-2" />
                )}
                {captureModal.eventType === 'clock_in' ? 'Clock In' : 'Clock Out'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Photo modal (unchanged) */}
      <Dialog
        open={viewPhotoModal.open}
        onOpenChange={(open) => { if (!open) setViewPhotoModal({ open: false, photo: null, name: '', timestamp: null, loading: false }); }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Clock-In Photo — {viewPhotoModal.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {viewPhotoModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : viewPhotoModal.photo ? (
              <>
                <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
                  <img
                    src={viewPhotoModal.photo}
                    alt={`Clock-in of ${viewPhotoModal.name}`}
                    className="w-full h-full object-contain"
                    data-testid="on-shift-photo-img"
                  />
                </div>
                {viewPhotoModal.timestamp && (
                  <p className="text-xs text-muted-foreground text-center">
                    Clocked in at {formatUK(viewPhotoModal.timestamp, "MMM d, yyyy 'at' HH:mm")}
                  </p>
                )}
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
